import React from 'react';
import {Layout, Typography} from 'antd';
import Header from "../../../components/molecules/Header/component";
import Footer from "../../../components/molecules/Footer/component";

const {Content} = Layout;
const {Title, Paragraph, Text} = Typography;

const TermsAndConditionsPage: React.FC = () => {
    return (
        <div>
            <Header/>
            <Content style={{maxWidth: 900, margin: '0 auto', padding: '32px 16px 48px'}}>
                <Typography>
                    <Title level={2}>Promotify AI – Terms &amp; Conditions</Title>

                    <Paragraph>
                        <Text strong>Effective date:</Text> 15/10/2025
                        <br/>
                        <Text strong>Company:</Text> Promotify AI Ltd (<Text italic>“Promotify”</Text>,{' '}
                        <Text italic>“we”</Text>, <Text italic>“us”</Text>, <Text italic>“our”</Text>)
                        <br/>
                        <Text strong>Platform:</Text> Promotify AI (the <Text italic>“Platform”</Text>)
                        <br/>
                        <Text strong>Users:</Text> Any individual or entity using the Platform (<Text
                        italic>“User”</Text>,{' '}<Text italic>“you”</Text>).
                    </Paragraph>

                    <Paragraph>
                        By accessing or using the Platform, including by creating an account, you agree to be bound by
                        these Terms &amp; Conditions (<Text italic>“Terms”</Text>). If you do not agree with these
                        Terms, you must not use the Platform.
                    </Paragraph>

                    <Title level={3}>1. Definitions</Title>
                    <Paragraph>
                        Promotify AI is a marketing platform that connects advertisers directly with promoters in order
                        to drive genuine engagement. Advertisers pay promoters on a per-click basis, while Promotify
                        validates each click and updates user balances once a click is confirmed as legitimate.
                    </Paragraph>

                    <Paragraph>
                        <Text strong>Advertiser Package – $95 / month:</Text> Includes unlimited campaign creation, the
                        ability to set budgets and click rates, access to a real-time analytics dashboard, and payment
                        only for clicks that are verified.
                    </Paragraph>
                    <Paragraph>
                        <Text strong>Promoter Package – $45 / month:</Text> Allows users to browse active campaigns,
                        generate unique affiliate links, earn for each verified click driven, and review detailed
                        performance reports.
                    </Paragraph>
                    <Paragraph>
                        <Text strong>Partner Package – $175 / month:</Text> Includes all Promoter features, plus a
                        40% rolling commission on referrals (for the lifetime of referred users) and 10% commission on
                        campaigns run by referred advertisers, along with priority support and early access to new
                        features.
                    </Paragraph>

                    <Paragraph>
                        All new users are eligible for a one-month free trial before billing begins. Subscription plans
                        and prices may change over time. Where this happens, Promotify will inform users of any
                        adjustments in advance.
                    </Paragraph>

                    <Title level={3}>2. Eligibility &amp; Account creation</Title>
                    <Paragraph>
                        To use the Platform, you must:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>Be at least 18 years old.</li>
                            <li>Provide accurate and complete information when creating and managing your account.</li>
                            <li>Pay all applicable subscription fees and charges when due.</li>
                            <li>Use the Platform only for lawful purposes and in compliance with these Terms.</li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        Promotify reserves the right to refuse, suspend, or terminate accounts where we reasonably
                        believe that information provided is inaccurate, the account is being misused, or these Terms
                        are being breached.
                    </Paragraph>

                    <Title level={3}>3. Use of the Platform</Title>
                    <Paragraph>
                        Promotify AI is designed to help businesses grow through genuine, organic clicks. The Platform
                        connects advertisers with promoters, who drive traffic to campaigns using unique links. Each
                        click is assessed to confirm authenticity and protect advertisers from fraudulent activity.
                    </Paragraph>
                    <Paragraph>
                        Advertisers, promoters, and partners each have specific roles and responsibilities as described
                        in these Terms. You agree to use the Platform only as intended and not to engage in any activity
                        that may harm the integrity or performance of the service.
                    </Paragraph>

                    <Title level={3}>4. User responsibilities</Title>
                    <Paragraph>
                        Users are responsible for their own spending, earnings, and any taxes that apply to them. You
                        must:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>Maintain the confidentiality of your login details and account.</li>
                            <li>Ensure that all actions taken through your account comply with these Terms.</li>
                            <li>Respect other users and refrain from abusive, misleading, or unlawful behaviour.</li>
                            <li>Use the Platform in accordance with applicable laws and regulations.</li>
                        </ul>
                    </Paragraph>

                    <Title level={3}>5. Campaigns &amp; Promotions</Title>
                    <Paragraph>
                        Promotify tracks and validates campaign activity with the aim of ensuring a fair and transparent
                        system for all users. We may use automated and manual checks to confirm that clicks are genuine
                        and to protect against fraudulent or artificially generated traffic.
                    </Paragraph>

                    <Title level={3}>6. Payments &amp; Fees</Title>
                    <Paragraph>
                        Subscription fees currently apply as follows:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>Advertiser: $95 per month</li>
                            <li>Promoter: $45 per month</li>
                            <li>Partner: $175 per month</li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        Withdrawal fees also apply; Advertisers - none, Promoters and Partners - 10%. These fees support
                        the running and maintenance of the Platform’s
                        infrastructure and services.
                        You are responsible for ensuring that your chosen payment method is valid and that any
                        subscription charges can be successfully processed.
                    </Paragraph>

                    <Title level={3}>7. Intellectual Property</Title>
                    <Paragraph>
                        All content, software, branding, logos, and other materials associated with Promotify AI are the
                        exclusive property of Promotify AI Ltd or its licensors. You are granted a limited, revocable
                        licence to access and use the Platform in accordance with these Terms.
                    </Paragraph>
                    <Paragraph>
                        You must not copy, reproduce, modify, distribute, or create derivative works from any part of
                        the Platform without our prior written consent. Any unauthorised use may result in legal
                        action.
                    </Paragraph>

                    <Title level={3}>8. Confidentiality &amp; Data Use</Title>
                    <Paragraph>
                        Promotify AI handles personal data in line with applicable data-protection legislation,
                        including GDPR. We do not sell or share your personal data with third parties for their own
                        marketing purposes.
                    </Paragraph>
                    <Paragraph>
                        We may use analytics tools, including PostHog, to understand Platform usage, improve user
                        journeys, measure conversion and retention, and monitor feature performance. More information
                        is provided in our Privacy Policy.
                    </Paragraph>
                    <Paragraph>
                        Confidential information that you share with us (for example, campaign details, internal
                        strategies, or non-public data) is treated as confidential and used only for providing and
                        improving the Platform and related services, unless disclosure is required by law.
                    </Paragraph>

                    <Title level={3}>9. Limitation of Liability</Title>
                    <Paragraph>
                        The Platform is provided on an “as is” and “as available” basis. While we aim to maintain a
                        secure, reliable service, we cannot guarantee that it will be uninterrupted or error-free.
                    </Paragraph>
                    <Paragraph>
                        Promotify AI is not responsible for losses caused by user actions, including how campaigns are
                        set up or promoted. Misuse of the Platform or breach of these Terms may result in account
                        suspension or termination and can be reported to relevant authorities where appropriate.
                    </Paragraph>

                    <Title level={3}>10. Termination &amp; Suspension</Title>
                    <Paragraph>
                        We may suspend or terminate your access to the Platform, with or without notice, where we
                        reasonably believe that:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>You promote inappropriate or prohibited content.</li>
                            <li>You use bots, click farms, or any artificial means to generate traffic.</li>
                            <li>You breach these Terms or applicable laws.</li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        Where an account is terminated for misuse or serious breach, any outstanding balances or
                        commissions associated with that account may be forfeited.
                    </Paragraph>

                    <Title level={3}>11. Governing Law &amp; Updates</Title>
                    <Paragraph>
                        These Terms are governed by and interpreted in accordance with the laws of England and Wales.
                        Any disputes arising from or relating to these Terms or your use of the Platform will be subject
                        to the exclusive jurisdiction of the courts of England and Wales.
                    </Paragraph>
                    <Paragraph>
                        Promotify may update these Terms from time to time. Updated versions will be posted on the
                        Platform, and continued use of the service after changes take effect will constitute your
                        acceptance of the revised Terms.
                    </Paragraph>
                </Typography>
            </Content>
            <Footer/>
        </div>
    );
};

export default TermsAndConditionsPage;
